import _ from "lodash";
import React, { useState, useEffect } from "react";

import { Popover, IconButton, Tooltip } from "@toddle-edu/ds-web";
import { TextAlignLeftOutlined } from "@toddle-edu/ds-icons";
import CircleLoading from "UIComponents/CircleLoading";
import MarkdownView from "UIComponents/MarkdownView";

import classes from "./GenerateTranscriptsButton.scss";
import classNames from "classnames";
import { useI18n } from "Hooks";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { isTouchableV3 } from "Utils";

const MAX_SCREEN_WIDTH = 1200;

const GenerateTranscriptsButton = props => {
  const {
    isStudent = false,
    content,
    audioTranscriptText,
    onGenerateTranscript,
    id,
    isChatBody = false,
    buttonSize = "medium",
  } = props;

  const hasGenerator = _.isFunction(onGenerateTranscript);

  const [openPopoverId, setOpenPopoverId] = useState(null);
  const [isLoading, setIsLoading] = useState(hasGenerator);
  const [transcript, setTranscript] = useState("");
  const { t } = useI18n(["common", "ai"]);

  useEffect(() => {
    if (!hasGenerator) return;

    const generate = async () => {
      try {
        setIsLoading(true);
        const text = await onGenerateTranscript();
        setTranscript(text);
      } finally {
        setIsLoading(false);
      }
    };

    generate();
  }, []);

  const handleClick = async e => {
    e.stopPropagation();

    setOpenPopoverId(prev => (prev === id ? null : id));
  };

  const isTouchableDevice = isTouchableV3(MAX_SCREEN_WIDTH);

  const isOpen = openPopoverId === id;

  const isDisabled = isStudent && _.isEmpty(audioTranscriptText) && isChatBody;

  const tooltip =
    isLoading || isDisabled
      ? t("ai:generating_transcript")
      : t("transcript:transcript");

  const transcriptContent = isStudent
    ? audioTranscriptText || transcript
    : content;

  return (
    <Popover
      isOpen={isOpen}
      isClosable={true}
      shouldCloseOnEsc={true}
      onCloseButtonClick={() => setOpenPopoverId(null)}
      onClickOutside={() => setOpenPopoverId(null)}
      containerStyle={{ zIndex: 9999 }}
      placement={isStudent ? "bottomLeft" : "bottomRight"}
      title={t("transcript:transcript")}
      body={
        <MarkdownView
          content={transcriptContent}
          contentContainerClass="text-body"
        />
      }
    >
      <span>
        <ConditionalWrapper
          condition={!isTouchableDevice && !isOpen}
          type="wrapperAsElement"
          wrapper={
            <Tooltip tooltip={tooltip} placement="top" showArrow={true} />
          }
        >
          <IconButton
            icon={
              isLoading ? (
                <CircleLoading size={"x-small"} />
              ) : (
                <TextAlignLeftOutlined />
              )
            }
            dsVersion={"2.0"}
            isActivated={isStudent && openPopoverId === id}
            variant="neutral"
            type="plain"
            size={buttonSize}
            onClick={handleClick}
            disabled={isDisabled}
            className={classNames({
              [classes.onHoverButton]: !isStudent,
              [classes.activeButton]: !isStudent && openPopoverId === id,
            })}
          />
        </ConditionalWrapper>
      </span>
    </Popover>
  );
};

export default GenerateTranscriptsButton;
